/*
 * The sixteen — the home page's own copy of the product registry.
 *
 * ── Why a copy ─────────────────────────────────────────────────────────────
 *
 * This list already exists twice in the repo, and neither copy can be
 * imported: both `ZodiacLauncher.jsx` and `ZodiacHub.jsx` keep it in
 * module-private constants, and both carry a "Copied from
 * indexx-exchange-backend/zodiac-embed — edit the source there, not here"
 * banner. An export added to either would survive exactly until the next
 * `sync.sh` run. Duplicating is the pattern those two components already
 * follow between themselves.
 *
 * ── Where the truth actually lives ─────────────────────────────────────────
 *
 * The backend, at /api/v1/zodiac/config. That endpoint is the reason a
 * seventeenth product is one backend change rather than fourteen redeploys,
 * and — the part that matters here — its hrefs are environment-aware: the
 * same call returns test.* on the test estate and the live hosts in
 * production, because the backend reads them from ZODIAC_HREFS. Hard-coding
 * product URLs on this page would quietly carry a signed-in tester off the
 * test environment mid-session.
 *
 * So the list below is a FALLBACK — it exists so the page never renders an
 * empty ecosystem when that call fails — and `fetchProducts` prefers the
 * live answer. Names, groups and order come from the live registry; art and
 * the one-line descriptions are local, because the registry carries ids, not
 * pictures or prose.
 */

export type GroupId = "earn" | "trade" | "play" | "spend" | "build";

export interface ZodiacProduct {
  id: string;
  name: string;
  group: GroupId;
  href: string;
  /** Dimmed and inert until the product ships. */
  comingSoon?: boolean;
  /** Filename under /logos. See LOGO_DARK for why there are two sets. */
  logo: string;
  /**
   * One line, under the name, in the ecosystem list.
   *
   * `null` where the copy does not exist yet — Crypto Treasury and xTokens
   * are literal [ONE LINE] placeholders on the approved design. Rendering
   * nothing is correct; inventing a plausible sentence is the one thing this
   * estate's own convention forbids (see yaysEcosystem.service.ts: a figure
   * with no source is an em dash, never a guess).
   */
  blurb: string | null;
}

export const GROUPS: { id: GroupId; label: string }[] = [
  { id: "earn", label: "Earn" },
  { id: "trade", label: "Trade" },
  { id: "play", label: "Play" },
  { id: "spend", label: "Spend" },
  { id: "build", label: "Build" },
];

/*
 * Art for a DARK surface.
 *
 * The launcher keeps two variants per product and picks by tone: `logo` for a
 * light panel, `logoBare` for a dark one. Five marks are black line art that
 * simply vanish on #0B0B0D — Academy, Wall Street, EMMM, Wallet and ReHuman —
 * so this map is the launcher's dark set, not its default set. Getting this
 * wrong does not error; it renders five invisible logos.
 */
const LOGO_DARK: Record<string, string> = {
  btcy: "btcy.png",
  yaysapp: "yaysapp.png",
  academy: "academy.png",
  exchange: "exchange.png",
  treasury: "treasury.png",
  wallstreet: "wallst-light.png",
  xtokens: "xtokens.png",
  usdxx: "usdxx.png",
  emmm: "emmm-dark.png",
  lotto: "lotto.png",
  pay: "pay.png",
  wallet: "wallet-dark.png",
  shoperpal: "shoperpal.png",
  rehuman: "rehuman-light.png",
  aiainai: "aiainai.png",
  xxemails: "xxemails.png",
};

/** One line each, from the approved design. `null` where none has been written. */
const BLURBS: Record<string, string | null> = {
  btcy: "Mine, buy and sell BTCY",
  yaysapp: "Chats, communities and calls",
  academy: "Courses",
  exchange: "Buy and sell",
  treasury: null, // [ONE LINE] on the design — not yet written
  wallstreet: "Tokenised assets",
  xtokens: null, // [ONE LINE] on the design — not yet written
  usdxx: "Soon",
  emmm: "Prediction markets",
  lotto: "Tickets and draws",
  pay: null,
  wallet: "Hold, send, receive, swap",
  shoperpal: "Marketplace",
  rehuman: "Longevity formulas",
  aiainai: "AI agents that do the work",
  xxemails: "Email campaigns",
};

/**
 * Last resort only. Hrefs here are the PRODUCTION hosts, so a page that falls
 * back to this on the test estate will link out to live — which is visible and
 * recoverable, where an empty ecosystem section is neither.
 *
 * Note ReHuman: the launcher's bundled copy of this list still marks it
 * comingSoon, and the live registry does not. Another reason to prefer the
 * network answer.
 */
const FALLBACK: ZodiacProduct[] = [
  { id: "btcy", name: "Bitcoin Yay", group: "earn", href: "https://bitcoinyay.com" },
  { id: "yaysapp", name: "YaysApp", group: "earn", href: "https://web.yaysapp.com" },
  { id: "academy", name: "Academy", group: "earn", href: "https://academy.indexx.ai" },
  { id: "exchange", name: "Exchange", group: "trade", href: "https://cex.indexx.ai" },
  { id: "treasury", name: "Crypto Treasury", group: "trade", href: "https://cex.indexx.ai/crypto-treasury" },
  { id: "wallstreet", name: "Wall Street", group: "trade", href: "https://wallstreet.indexx.ai" },
  { id: "xtokens", name: "xTokens", group: "trade", href: "https://indexx.ai/xtokens" },
  { id: "usdxx", name: "USDXX", group: "trade", href: "https://indexx.ai", comingSoon: true },
  { id: "emmm", name: "Eeny Meeny Miny Moe", group: "play", href: "https://eenymeenyminymoe.io" },
  { id: "lotto", name: "Fantasy Lotto", group: "play", href: "https://lotto.indexx.ai" },
  { id: "wallet", name: "Indexx Wallet", group: "spend", href: "https://wallet.indexx.ai" },
  { id: "shoperpal", name: "ShoperPal", group: "spend", href: "https://shop.indexx.ai" },
  { id: "rehuman", name: "ReHuman", group: "spend", href: "https://rehumenlab.com" },
  { id: "pay", name: "Indexx Pay", group: "spend", href: "https://pay.indexx.ai" },
  { id: "aiainai", name: "ai ai N ai", group: "build", href: "https://aiainai.ai" },
  { id: "xxemails", name: "XXemails", group: "build", href: "https://xxemails.indexx.ai/login" },
].map((p) => ({ ...p, logo: LOGO_DARK[p.id], blurb: BLURBS[p.id] ?? null })) as ZodiacProduct[];

/** Where the launcher looks, and the same fallback chain. */
const API_BASE =
  process.env.REACT_APP_ZODIAC_API?.replace(/\/+$/, "") || "https://api.v1.indexx.ai";

const isGroup = (g: unknown): g is GroupId =>
  typeof g === "string" && GROUPS.some((x) => x.id === g);

/**
 * The live sixteen, falling back to the list above.
 *
 * Never rejects: an ecosystem section is not worth taking the home page down
 * for, and the fallback is a correct — if environment-blind — answer.
 */
export async function fetchProducts(signal?: AbortSignal): Promise<ZodiacProduct[]> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/zodiac/config`, { signal });
    if (!res.ok) return FALLBACK;
    const body = await res.json();
    const rows = body?.data?.products;
    if (!Array.isArray(rows) || rows.length === 0) return FALLBACK;

    const live = rows
      .filter((r: any) => r && typeof r.id === "string" && isGroup(r.group))
      .map((r: any) => ({
        id: r.id,
        name: typeof r.name === "string" ? r.name : r.id,
        group: r.group as GroupId,
        href: r.id === "pay" ? "https://pay.indexx.ai" : typeof r.href === "string" ? r.href : "#",
        comingSoon: r.id === "pay" ? false : Boolean(r.comingSoon),
        // Art and prose stay local — the registry carries neither.
        logo: LOGO_DARK[r.id] || "",
        blurb: BLURBS[r.id] ?? null,
      }))
      // A product the registry knows about but we have no mark for would
      // render as a broken image in a row of logos. Drop it rather than
      // show a gap; the fallback still has the sixteen we do have art for.
      .filter((p: ZodiacProduct) => p.logo !== "");

    return live.length > 0 ? live : FALLBACK;
  } catch (_) {
    return FALLBACK;
  }
}

/** The sixteen split into the five columns the design draws, in order. */
export function byGroup(products: ZodiacProduct[]) {
  return GROUPS.map((g) => ({
    ...g,
    items: products.filter((p) => p.group === g.id),
  }));
}
